import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const ADMIN_LIST_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type AdminListPageSize = (typeof ADMIN_LIST_PAGE_SIZE_OPTIONS)[number];
export type AdminListSortOrder = "asc" | "desc";

type StringFilterRecord<TFilters> = { [K in keyof TFilters]: string };

type UseAdminListStateOptions<TSortKey extends string, TFilters extends StringFilterRecord<TFilters>> = {
  defaultSort: TSortKey;
  defaultOrder?: AdminListSortOrder;
  defaultFilters: TFilters;
  validSorts: readonly TSortKey[];
  validateFilter?: <TKey extends keyof TFilters>(key: TKey, value: string) => TFilters[TKey];
};

export type AdminListQueryState<TSortKey extends string, TFilters extends StringFilterRecord<TFilters>> = {
  appliedQuery: string;
  draftQuery: string;
  q: string;
  filters: TFilters;
  sort: TSortKey;
  order: AdminListSortOrder;
  page: number;
  pageSize: AdminListPageSize;
  setDraftQuery: (value: string) => void;
  submitSearch: () => void;
  setFilter: <TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey]) => void;
  setSort: (key: TSortKey) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: AdminListPageSize) => void;
};

function parsePage(value: string | null): number {
  const parsed = Number.parseInt(value ?? "1", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function parsePageSize(value: string | null): AdminListPageSize {
  const parsed = Number.parseInt(value ?? "25", 10);
  return ADMIN_LIST_PAGE_SIZE_OPTIONS.includes(parsed as AdminListPageSize)
    ? (parsed as AdminListPageSize)
    : 25;
}

function parseOrder(value: string | null, fallback: AdminListSortOrder): AdminListSortOrder {
  return value === "desc" || value === "asc" ? value : fallback;
}

function writeSearchParams<TSortKey extends string, TFilters extends StringFilterRecord<TFilters>>(
  params: URLSearchParams,
  next: {
    q?: string;
    filters?: Partial<TFilters>;
    sort?: TSortKey;
    order?: AdminListSortOrder;
    page?: number;
    pageSize?: AdminListPageSize;
  },
  defaults: {
    defaultSort: TSortKey;
    defaultOrder: AdminListSortOrder;
    defaultFilters: TFilters;
  }
): URLSearchParams {
  const nextParams = new URLSearchParams(params);

  if (next.q !== undefined) {
    if (next.q.trim()) nextParams.set("q", next.q);
    else nextParams.delete("q");
  }

  if (next.filters) {
    for (const key of Object.keys(next.filters) as Array<keyof TFilters>) {
      const value = next.filters[key];
      const defaultValue = defaults.defaultFilters[key];
      const paramKey = String(key);
      if (value && value !== defaultValue) nextParams.set(paramKey, value);
      else nextParams.delete(paramKey);
    }
  }

  if (next.sort !== undefined) {
    if (next.sort === defaults.defaultSort) nextParams.delete("sort");
    else nextParams.set("sort", next.sort);
  }

  if (next.order !== undefined) {
    if (next.order === defaults.defaultOrder) nextParams.delete("order");
    else nextParams.set("order", next.order);
  }

  if (next.page !== undefined) {
    if (next.page <= 1) nextParams.delete("page");
    else nextParams.set("page", String(next.page));
  }

  if (next.pageSize !== undefined) {
    if (next.pageSize === 25) nextParams.delete("pageSize");
    else nextParams.set("pageSize", String(next.pageSize));
  }

  return nextParams;
}

export function useAdminListState<TSortKey extends string, TFilters extends StringFilterRecord<TFilters>>({
  defaultSort,
  defaultOrder = "asc",
  defaultFilters,
  validSorts,
  validateFilter
}: UseAdminListStateOptions<TSortKey, TFilters>): AdminListQueryState<TSortKey, TFilters> {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [draftQuery, setDraftQuery] = useState(q);
  const sortCandidate = searchParams.get("sort") as TSortKey | null;
  const sort = sortCandidate && validSorts.includes(sortCandidate) ? sortCandidate : defaultSort;
  const order = parseOrder(searchParams.get("order"), defaultOrder);
  const page = parsePage(searchParams.get("page"));
  const pageSize = parsePageSize(searchParams.get("pageSize"));
  const filters = useMemo(() => {
    const next = { ...defaultFilters };

    for (const key of Object.keys(defaultFilters) as Array<keyof TFilters>) {
      const rawValue = searchParams.get(String(key)) ?? defaultFilters[key];
      next[key] = validateFilter ? validateFilter(key, rawValue) : (rawValue as TFilters[typeof key]);
    }

    return next;
  }, [defaultFilters, searchParams, validateFilter]);

  useEffect(() => {
    setDraftQuery(q);
  }, [q]);

  return {
    appliedQuery: q,
    draftQuery,
    q,
    filters,
    sort,
    order,
    page,
    pageSize,
    setDraftQuery,
    submitSearch: () =>
      setSearchParams((current) =>
        writeSearchParams(current, { q: draftQuery.trim(), page: 1 }, { defaultSort, defaultOrder, defaultFilters })
      ),
    setFilter: (key, value) =>
      setSearchParams((current) =>
        writeSearchParams(current, { filters: { [key]: value } as unknown as Partial<TFilters>, page: 1 }, { defaultSort, defaultOrder, defaultFilters })
      ),
    setSort: (key) =>
      setSearchParams((current) => {
        const currentSort = current.get("sort") ?? defaultSort;
        const currentOrder = parseOrder(current.get("order"), defaultOrder);
        const nextOrder = currentSort === key ? (currentOrder === "asc" ? "desc" : "asc") : "asc";
        return writeSearchParams(current, { sort: key, order: nextOrder, page: 1 }, { defaultSort, defaultOrder, defaultFilters });
      }),
    setPage: (nextPage) =>
      setSearchParams((current) =>
        writeSearchParams(current, { page: Math.max(1, nextPage) }, { defaultSort, defaultOrder, defaultFilters })
      ),
    setPageSize: (nextPageSize) =>
      setSearchParams((current) =>
        writeSearchParams(current, { pageSize: nextPageSize, page: 1 }, { defaultSort, defaultOrder, defaultFilters })
      )
  };
}
